import React, { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import "@/App.css";
import { FloatingNavbar } from "@/components/FloatingNavbar";
import { StatisticsSection } from "@/components/StatisticsSection";
import { IndustriesSection } from "@/components/IndustriesSection";
import { SpecialtiesGrid } from "@/components/SpecialtiesGrid";
import { WhyNovaCore } from "@/components/WhyNovaCore";
import { ProcessTimeline } from "@/components/ProcessTimeline";
import { LatestOpportunities } from "@/components/LatestOpportunities";
import { ContactSection } from "@/components/ContactSection";
import { Footer } from "@/components/Footer";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { AdminAuthProvider, ProtectedRoute } from "@/context/AdminAuthContext";
import { ScrollToTopButton } from "@/components/ScrollToTopButton";

// Route-based code splitting: keep the marketing homepage eager, lazy-load
// the heavy ATS console, careers, job detail and legal pages.
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const CareersPage = lazy(() => import("@/pages/CareersPage"));
const JobDetailPage = lazy(() => import("@/pages/JobDetailPage"));
// Defer the heavy 3D globe (three.js / react-globe.gl) out of the initial bundle.
const InteractiveGlobe = lazy(() =>
  import("@/components/InteractiveGlobe").then((m) => ({ default: m.InteractiveGlobe }))
);

const RouteFallback = () => (
  <div className="min-h-screen bg-[#05080A] flex items-center justify-center text-slate-400 font-mono text-sm animate-pulse">LOADING…</div>
);

function PublicScrollTop() {
  const location = useLocation();
  if (location.pathname.startsWith("/admin")) return null;
  return <ScrollToTopButton />;
}

function LandingPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const handleScrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      // Offset a bit for floating nav
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  // Scroll to hash target (e.g. navigating from another page to /#contact)
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      setTimeout(() => handleScrollToSection(id), 300);
    }
  }, [location.hash]);

  return (
    <div 
      data-testid="app-root"
      className="min-h-screen bg-[#05080A] text-slate-100 selection:bg-[#65FFB6]/30 selection:text-[#65FFB6] relative overflow-hidden"
    >
      <SEO
        title="NovaCore Staffing | Global Data Centre Recruitment"
        description="Powering the world's digital infrastructure by connecting elite data centre, AI infrastructure and mission-critical professionals with leading employers worldwide."
        path="/"
      />
      {/* 1. Cinematic Dark Space Background */}
      {/* Stars Backdrop */}
      <div className="absolute inset-0 z-0 opacity-20 pointer-events-none bg-[radial-gradient(#ffffff_0.8px,transparent_0.8px)] [background-size:24px_24px]"></div>
      
      {/* Moving Ambient Radial Light Blurs */}
      <div className="absolute top-[10%] left-[-10%] w-[500px] h-[500px] rounded-full bg-emerald-950/10 blur-[120px] pointer-events-none animate-pulse-glow z-0"></div>
      <div className="absolute top-[40%] right-[-10%] w-[600px] h-[600px] rounded-full bg-[#0B1F1A]/20 blur-[150px] pointer-events-none animate-pulse-glow z-0" style={{ animationDelay: "2s" }}></div>
      <div className="absolute bottom-[10%] left-[20%] w-[450px] h-[450px] rounded-full bg-emerald-900/10 blur-[100px] pointer-events-none animate-pulse-glow z-0" style={{ animationDelay: "1s" }}></div>

      {/* 2. Floating Navigation Bar */}
      <FloatingNavbar 
        onScrollToSection={handleScrollToSection} 
      />

      {/* 3. Hero Section (Full-screen, split content & globe) */}
      <header 
        data-testid="hero-section"
        className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 min-h-screen flex flex-col lg:flex-row items-center justify-between pt-32 pb-16 gap-12"
      >
        {/* Hero Left Side Panel */}
        <div 
          data-testid="hero-content-panel"
          className="w-full lg:w-[48%] flex flex-col items-start text-left gap-6 relative z-25"
        >
          {/* Large Headline */}
          <h1 
            data-testid="hero-headline"
            className="text-4xl sm:text-5xl lg:text-6xl font-outfit font-bold tracking-tighter leading-[1.08] text-white"
          >
            Powering the <br />
            <span className="text-emerald-gradient inline-block animate-text-shimmer">World&apos;s Digital Infrastructure</span> <br />
            Through People.
          </h1>

          {/* Supporting Paragraph */}
          <p 
            data-testid="hero-subtext"
            className="text-slate-400 font-manrope text-base sm:text-lg leading-relaxed max-w-lg"
          >
            NovaCore Staffing connects elite engineers, technicians, commissioning specialists, and mission-critical professionals with the companies building tomorrow&apos;s digital infrastructure across every continent.
          </p>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-4 mt-2">
            <Button
              data-testid="hero-hire-talent-btn"
              onClick={() => handleScrollToSection("contact")}
              className="bg-[#65FFB6] hover:bg-[#A4FFD6] text-black font-outfit font-semibold px-8 h-12 rounded-full shadow-[0_0_20px_rgba(101,255,182,0.4)] transition-all duration-300 hover:scale-[1.03]"
            >
              Hire Elite Talent
            </Button>
            <Button
              data-testid="hero-explore-careers-btn"
              onClick={() => navigate("/careers")}
              variant="outline"
              className="border-white/10 text-white hover:bg-white/5 hover:text-white rounded-full font-outfit font-semibold px-8 h-12 transition-all duration-300"
            >
              Explore Careers
            </Button>
          </div>

          {/* technology tags grid */}
          <div 
            data-testid="hero-tech-tags"
            className="flex flex-wrap gap-2.5 mt-6 border-t border-white/5 pt-6 w-full"
          >
            {[
              "AI Infrastructure",
              "Data Centers",
              "Critical Facilities",
              "Cloud Computing",
              "Global Engineering Talent",
              "Networking"
            ].map((tag) => (
              <span
                key={tag}
                data-testid={`hero-tag-${tag.toLowerCase().replace(" ", "-")}`}
                className="text-xs font-mono font-semibold text-slate-400 bg-white/5 border border-white/10 px-3 py-1.5 rounded-full hover:border-[#65FFB6]/30 hover:text-white transition-colors cursor-default"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Hero Right Side Panel (Rotating Earth Globe) */}
        <div 
          data-testid="hero-globe-panel"
          className="w-full lg:w-[48%] flex items-center justify-center relative min-h-[400px] md:min-h-[500px]"
        >
          <Suspense fallback={<div className="w-full h-[400px] md:h-[500px] flex items-center justify-center"><div className="w-40 h-40 rounded-full bg-[#0B1F1A]/40 blur-2xl animate-pulse" /></div>}>
            <InteractiveGlobe />
          </Suspense>
        </div>
      </header>

      {/* 4. Statistics Panel Section */}
      <StatisticsSection />

      {/* 5. Industries (Tetris Bento Grid) Section */}
      <IndustriesSection />

      {/* Latest Opportunities — 4 newest published jobs */}
      <LatestOpportunities onViewAll={() => navigate("/careers")} />

      {/* 6. Specialties Recruitment Grid Section */}
      <SpecialtiesGrid />

      {/* 8. Why NovaCore (Scroll features) Section */}
      <WhyNovaCore />

      {/* 9. Recruitment Process (Horizontal Timeline) Section */}
      <ProcessTimeline />

      {/* 13. Contact Section (Hire request & form) */}
      <ContactSection />

      {/* 14. Footer Section */}
      <Footer />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AdminAuthProvider>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/jobs/:slug" element={<JobDetailPage />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          </Routes>
        </Suspense>
        {/* Centralized Sonner Toaster container for elegant premium notifications */}
        <Toaster position="bottom-right" theme="dark" />
        <PublicScrollTop />
      </AdminAuthProvider>
    </BrowserRouter>
  );
}

export default App;
